import React from 'react';
import { isAndroid, isKakaoInApp } from '../utils/UserAgentUtils';

type Props = {
  getTargetUrl?: () => string;
  preferredHost?: string;
  enable?: boolean;
  delayMs?: number;
};

function buildTargetUrl(rawHref: string, preferredHost?: string): URL {
  const u = new URL(rawHref);
  if (preferredHost) u.host = preferredHost;
  if (!u.searchParams.get('external')) u.searchParams.set('external', '1');
  return u;
}

function intentForAndroid(u: URL, opts?: { packageName?: string; chooser?: boolean }) {
  const scheme = u.protocol.replace(':', '');
  const base = `intent://${u.host}${u.pathname}${u.search}${u.hash}#Intent;scheme=${scheme};`;
  if (opts?.chooser) return `${base}end`;
  const pkgName = opts?.packageName ?? 'com.android.chrome';
  const pkg = `package=${pkgName};`;
  const fb = `S.browser_fallback_url=${encodeURIComponent('https://play.google.com/store/apps/details?id=' + pkgName)};`;
  return `${base}${pkg}${fb}end`;
}

export const AndroidKakaoRedirect = ({ getTargetUrl, preferredHost, enable = true, delayMs = 0 }: Props) => {
  React.useEffect(() => {
    if (!enable) return;
    if (!isAndroid() || !isKakaoInApp()) return;

    const here = new URL(window.location.href);
    if (here.searchParams.get('external') === '1') return;

    const raw = getTargetUrl ? getTargetUrl() : window.location.href;
    const url = buildTargetUrl(raw, preferredHost);

    const jump = () => {
      const intentChrome = intentForAndroid(url, { packageName: 'com.android.chrome' });
      const intentChooser = intentForAndroid(url, { chooser: true });

      const try1 = () => {
        try {
          window.location.href = intentChrome;
        } catch (e) {
          console.error('Intent redirect failed', e);
        }
        return new Promise<void>(r => setTimeout(r, 700));
      };
      const try2 = () => {
        try {
          window.location.href = intentChooser;
        } catch (e) {
          console.error('Intent redirect failed', e);
        }
        return new Promise<void>(r => setTimeout(r, 500));
      };
      const try3 = () => {
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      };

      try1().then(try2).then(try3);
    };

    if (delayMs > 0) {
      const t = setTimeout(jump, delayMs);
      return () => clearTimeout(t);
    } else {
      jump();
    }
  }, [enable, getTargetUrl, preferredHost, delayMs]);

  return null;
};
